import React, { useRef, useState, useEffect } from "react";
import { any, bool, func, object, string, array, number } from "prop-types";
import { useTranslation } from "@lib/i18n";

import CollapsableContainer from "@components/shared/CollapsableContainer";
import StateMachine from "../shared/StateMachine";
import AddressSelection from "./steps/AddressSelection";
import Confirmation from "./steps/Confirmation";
import MapSelection from "./steps/MapSelection/MapSelection";
import TypeSelection from "./steps/TypeSelection";
import DeliveryContactSelect from "./steps/DeliveryContactSelect";

import gtm from "@gtm/core";
import { getFinanceVariant } from "@lib/getFinanceVariant";
import { financeSimulationType } from "@types";
import { useIsB2BSelected } from "@hooks/useIsB2BSelected";
import { revertProgressEvent } from "@gtm/events/shared";
import { STEPS } from "@gtm/constants";
import { useCarValidation } from "@hooks/useCarValidation";
import api from "@api";
import { useB2BDeliveryContactSelector } from "@redux/reducers/appConfig";
import { useDeliveryMethods } from "@hooks/useDeliveryMethods";
import { useBasketErrorCodesSelector } from "@redux/reducers/errors";
import { useScrollToView } from "@hooks/useScrollToView";

import {
  COLLAPSIBLE_ANIM_TIMER,
  REQUEST_ERROR_CODES,
  DELIVERY_METHOD,
} from "@shared/constants";

import {
  useSalesmanSelector,
  useDealDeliveryMethodSelector,
  useBusinessModelSelector,
  useDeliveryContactSelector,
  useDealSelector,
} from "@redux/reducers/deal";
import { useIsKeyAccount } from "@redux/reducers/auth";

const initialDeliveryContactInfo = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
};

const Delivery = ({
  initialDeliveryPoints,
  affiliation,
  deliveryAddress,
  deliverySelected,
  disabled,
  errorMessage,
  primaryScreenErrorMessage,
  stateMachineKey,
  expectedDeliveryDate,
  financeSimulation,
  isHomeDelivery,
  isOpen,
  setDeliveryAddress,
  openDelivery,
  closeDelivery,
  onConfirmSuccess,
  setIsHomeDelivery,
  onModification,
  isSSRRendered,
  fixedHeaderHeight,
}) => {
  const { t } = useTranslation();

  const containerRef = useRef(null);
  const financeVariant = getFinanceVariant(financeSimulation.journey);
  const [inputMapAddress, setInputMapAddress] = useState(null);
  const [latLng, setLatLng] = useState(null);
  const businessModel = useBusinessModelSelector();
  const salesman = useSalesmanSelector();
  const isB2BSelected = useIsB2BSelected();
  const deliveryContact = useDeliveryContactSelector();
  const deal = useDealSelector();
  const {
    isDeliveryPointUnavailable,
    isDeliveryMethodUnavailable,
  } = useCarValidation(deal);
  const dealDeliveryMethod = useDealDeliveryMethodSelector();
  const deliveryMethods = useDeliveryMethods(businessModel);
  const scrollToView = useScrollToView();

  const deliveryContactConfig = useB2BDeliveryContactSelector();
  const isKeyAccount = useIsKeyAccount();
  const deliveryContactEnabled = deliveryContactConfig.enabled || isKeyAccount;

  const [deliveryPoints, setDeliveryPoints] = useState(initialDeliveryPoints);

  const basketErrorCodes = useBasketErrorCodesSelector();

  const [isDeliveryContactSelected, setIsDeliveryContactSelected] = useState(
    null
  );

  const [deliveryContactInfo, setDeliveryContactInfo] = useState(
    deliveryContact ?? initialDeliveryContactInfo
  );

  const STATE_IDS = {
    TYPE_SELECTION: "TYPE_SELECTION",
    RECEIVING_CONTACT: "RECEIVING_CONTACT",
    ADDRESS: "ADDRESS",
    MAP: "MAP",
    CONFIRMATION: "CONFIRMATION",
  };

  const resetScrollPosition = () => {
    scrollToView(containerRef);
  };

  useEffect(() => {
    const resetScrollTimeout = setTimeout(() => {
      if (!disabled) {
        resetScrollPosition();
      }
    }, COLLAPSIBLE_ANIM_TIMER.resetToScrollTimeout);
    return () => {
      clearTimeout(resetScrollTimeout);
    };
  }, [disabled]);

  useEffect(() => {
    if (!isDeliveryPointUnavailable) {
      return;
    }

    api.getDeliveryPoints().then(({ data }) => {
      setDeliveryPoints(data);
      onModification();
      gtm.fire(revertProgressEvent(STEPS.DELIVERY));
    });
  }, [primaryScreenErrorMessage]);

  const salesmanPoint = deliveryPoints
    ? deliveryPoints
        .filter(deliveryPoint => deliveryPoint.salesmanId)
        .find(
          deliveryPoint =>
            deliveryPoint.salesmanId?.toLowerCase() === salesman?.toLowerCase()
        )
    : null;

  return (
    <CollapsableContainer
      title={t("basket.delivery.title")}
      titleIndex={3}
      ref={containerRef}
      isDisabled={disabled}
      onChange={isOpen => {
        if (isOpen) {
          closeDelivery();

          return;
        }

        openDelivery();
      }}
      isOpen={
        !disabled &&
        (isOpen ||
          basketErrorCodes.includes(REQUEST_ERROR_CODES.LEAD_TIME_CHANGED))
      }
    >
      {(isOpen || deliverySelected) && (
        <StateMachine
          key={stateMachineKey}
          initialStateId={
            deliverySelected && !isDeliveryMethodUnavailable
              ? STATE_IDS.CONFIRMATION
              : null
          }
          onNextState={(
            currentStateId,
            { resetState, isHomeDelivery, isSalesmanPoint }
          ) => {
            resetScrollPosition();

            if (resetState) {
              setLatLng({});
              setInputMapAddress(null);
              openDelivery();
              setDeliveryAddress(null);
              setIsHomeDelivery(false);

              return STATE_IDS.TYPE_SELECTION;
            }

            switch (currentStateId) {
              case STATE_IDS.TYPE_SELECTION: {
                if (isHomeDelivery || isSalesmanPoint) {
                  return isB2BSelected && deliveryContactEnabled
                    ? STATE_IDS.RECEIVING_CONTACT
                    : STATE_IDS.CONFIRMATION;
                }

                return STATE_IDS.ADDRESS;
              }

              case STATE_IDS.RECEIVING_CONTACT:
                return STATE_IDS.CONFIRMATION;

              case STATE_IDS.ADDRESS:
                return isHomeDelivery ? STATE_IDS.CONFIRMATION : STATE_IDS.MAP;

              case STATE_IDS.MAP:
                return isB2BSelected && deliveryContactEnabled
                  ? STATE_IDS.RECEIVING_CONTACT
                  : STATE_IDS.CONFIRMATION;

              default:
                return STATE_IDS.TYPE_SELECTION;
            }
          }}
          onPrevState={(currentStateId, { isSalesmanPoint }) => {
            resetScrollPosition();

            switch (currentStateId) {
              case STATE_IDS.MAP:
                return STATE_IDS.ADDRESS;

              case STATE_IDS.RECEIVING_CONTACT:
                return isHomeDelivery
                  ? STATE_IDS.TYPE_SELECTION
                  : STATE_IDS.MAP;

              case STATE_IDS.ADDRESS:
              default:
                return STATE_IDS.TYPE_SELECTION;

              case STATE_IDS.CONFIRMATION: {
                if (isB2BSelected && deliveryContactEnabled) {
                  return STATE_IDS.RECEIVING_CONTACT;
                }

                return isHomeDelivery || isSalesmanPoint
                  ? STATE_IDS.TYPE_SELECTION
                  : STATE_IDS.MAP;
              }
            }
          }}
          states={[
            {
              id: STATE_IDS.TYPE_SELECTION,
              component: (
                <TypeSelection
                  setIsHomeDelivery={setIsHomeDelivery}
                  primaryScreenErrorMessage={primaryScreenErrorMessage}
                  salesmanPoint={salesmanPoint}
                  setDeliveryAddress={setDeliveryAddress}
                  deliveryPoints={deliveryPoints}
                  fixedHeaderHeight={fixedHeaderHeight}
                />
              ),
            },
            {
              id: STATE_IDS.RECEIVING_CONTACT,
              component: (
                <DeliveryContactSelect
                  isDeliveryContactSelected={isDeliveryContactSelected}
                  setIsDeliveryContactSelected={setIsDeliveryContactSelected}
                  deliveryContactInfo={deliveryContactInfo}
                  setDeliveryContactInfo={setDeliveryContactInfo}
                  initialDeliveryContactInfo={initialDeliveryContactInfo}
                />
              ),
            },
            {
              id: STATE_IDS.ADDRESS,
              component: (
                <AddressSelection
                  affiliation={affiliation}
                  inputMapAddress={inputMapAddress}
                  setDeliveryAddress={setDeliveryAddress}
                  pageVariant={financeVariant}
                  setInputMapAddress={setInputMapAddress}
                  setLatLng={setLatLng}
                  isSSRRendered={isSSRRendered}
                  titleLabel={
                    deliveryMethods.find(
                      method => method.type === DELIVERY_METHOD.DELIVERY_POINT
                    )?.label
                  }
                />
              ),
            },
            {
              id: STATE_IDS.MAP,
              component: (
                <MapSelection
                  affiliation={affiliation}
                  deliveryPoints={
                    deliveryPoints?.filter(
                      deliveryPoint => deliveryPoint.id !== salesmanPoint?.id
                    ) ?? []
                  }
                  deliveryAddress={deliveryAddress}
                  setDeliveryAddress={setDeliveryAddress}
                  inputMapAddress={inputMapAddress}
                  setInputMapAddress={setInputMapAddress}
                  latLng={latLng}
                  pageVariant={financeVariant}
                  setLatLng={setLatLng}
                  isSSRRendered={isSSRRendered}
                />
              ),
            },
            {
              id: STATE_IDS.CONFIRMATION,
              component: (
                <Confirmation
                  deliveryMethodLabel={dealDeliveryMethod.label}
                  affiliation={affiliation}
                  key="confirmation"
                  deliveryAddress={deliveryAddress}
                  expectedDeliveryDate={expectedDeliveryDate}
                  deliverySelected={deliverySelected}
                  pageVariant={financeVariant}
                  onConfirmSuccess={onConfirmSuccess}
                  errorMessage={errorMessage}
                  primaryScreenErrorMessage={primaryScreenErrorMessage}
                  isHomeDelivery={isHomeDelivery}
                  onModification={onModification}
                  isOpen={isOpen}
                  isB2BSelected={isB2BSelected}
                  isSSRRendered={isSSRRendered}
                  fixedHeaderHeight={fixedHeaderHeight}
                />
              ),
            },
          ]}
        />
      )}
    </CollapsableContainer>
  );
};

Delivery.propTypes = {
  onConfirmSuccess: func.isRequired,
  onModification: func.isRequired,
  openDelivery: func.isRequired,
  closeDelivery: func.isRequired,
  setDeliveryAddress: func.isRequired,
  setIsHomeDelivery: func.isRequired,
  expectedDeliveryDate: string.isRequired,
  deliverySelected: bool.isRequired,
  initialDeliveryPoints: array,
  affiliation: string,
  disabled: bool,
  detailSectionRef: any,
  deliveryAddress: object,
  errorMessage: string,
  primaryScreenErrorMessage: string,
  stateMachineKey: string,
  financeSimulation: financeSimulationType,
  isHomeDelivery: bool,
  isOpen: bool,
  isSSRRendered: bool,
  fixedHeaderHeight: number,
};

export default Delivery;
